import { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Loader2, XCircle, Package, Truck, Clock, ExternalLink, Gift } from "lucide-react";

const MAX_ATTEMPTS = 8;
const INTERVAL = 2000;

const STATUS_MAP = {
  processing: { label: "Payment received — building your order", icon: Clock },
  submitted: { label: "In production", icon: Package },
  in_production: { label: "In production", icon: Package },
  shipped: { label: "Shipped", icon: Truck },
  delivered: { label: "Delivered", icon: CheckCircle2 },
  printify_failed: { label: "Payment received", icon: CheckCircle2 },
};

export default function OrderSuccess() {
  const { search } = useLocation();
  const { clear } = useCart();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [state, setState] = useState(sessionId || orderId ? "checking" : "success");
  const [order, setOrder] = useState(null);
  const clearedRef = useRef(false);

  const clearOnce = () => {
    if (!clearedRef.current) {
      clear();
      clearedRef.current = true;
    }
  };

  // Stripe path (session_id)
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let active = true;
    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        if (r.data.payment_status === "paid") {
          setOrder(r.data.order || { total_price: r.data.amount_total });
          setState("success");
          clearOnce();
          return;
        }
        if (r.data.status === "expired") return setState("failed");
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) return setState("timeout");
        setTimeout(poll, INTERVAL);
      } catch {
        if (active) setState("failed");
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // PayPal path (order_id) — poll while fulfillment finishes in the background
  useEffect(() => {
    if (sessionId || !orderId) return;
    let attempts = 0;
    let active = true;
    const poll = async () => {
      try {
        const r = await api.get(`/orders/${orderId}`);
        if (!active) return;
        setOrder(r.data);
        setState("success");
        clearOnce();
        // keep polling while the background fulfillment is still running
        if (r.data.status === "processing" && attempts < MAX_ATTEMPTS) {
          attempts += 1;
          setTimeout(poll, INTERVAL);
        }
      } catch {
        if (!active) return;
        // order not found yet is unusual; still show success
        setState("success");
        clearOnce();
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, sessionId]);

  if (state === "checking") {
    return (
      <div data-testid="order-checking" className="max-w-2xl mx-auto px-6 py-32 text-center bg-background min-h-screen">
        <Loader2 className="w-14 h-14 animate-spin text-primary mx-auto" />
        <h1 className="font-anton text-3xl uppercase tracking-tight mt-8 text-white">Confirming payment…</h1>
        <p className="text-white/50 mt-3">Hang tight, this only takes a moment.</p>
      </div>
    );
  }

  if (state === "failed" || state === "timeout") {
    return (
      <div data-testid="order-failed" className="max-w-2xl mx-auto px-6 py-32 text-center bg-background min-h-screen">
        <XCircle className="w-14 h-14 text-red-500 mx-auto" />
        <h1 className="font-anton text-3xl uppercase tracking-tight mt-8 text-white">
          {state === "timeout" ? "Still processing" : "Payment not completed"}
        </h1>
        <p className="text-white/50 mt-3">
          {state === "timeout"
            ? "Your payment is taking longer than usual. Check your email for confirmation."
            : "We couldn't confirm your payment. If you were charged, contact us and we'll sort it out."}
        </p>
        <Link to="/checkout" className="inline-block mt-8 bg-primary text-black hover:bg-white px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm transition-colors">
          Back to Checkout
        </Link>
      </div>
    );
  }

  const statusKey = order?.status || "processing";
  const statusInfo = STATUS_MAP[statusKey] || STATUS_MAP.processing;
  const StatusIcon = statusInfo.icon;
  const tracking = order?.tracking;
  const isFulfilling = statusKey === "processing";

  return (
    <div data-testid="order-success" className="max-w-2xl mx-auto px-6 py-24 bg-background min-h-screen">
      <div className="text-center">
        <CheckCircle2 className="w-16 h-16 text-primary mx-auto" />
        <h1 className="font-anton text-4xl sm:text-6xl uppercase tracking-tight mt-8 text-white">Order confirmed</h1>
        <p className="text-white/60 mt-4">
          Thanks for repping the blend. A confirmation is on its way to your inbox.
        </p>
      </div>

      {order && (
        <div className="mt-10 border border-white/10 bg-card text-left p-6" data-testid="order-summary">
          {order.id && (
            <div className="flex justify-between text-sm mb-3">
              <span className="text-white/50 uppercase tracking-widest">Order ID</span>
              <span className="font-bold text-white" data-testid="order-id">{order.id.slice(0, 8)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm mb-3">
            <span className="text-white/50 uppercase tracking-widest">Payment</span>
            <span className="font-bold uppercase text-primary">Paid</span>
          </div>

          {/* Fulfillment status */}
          <div className="flex justify-between items-center text-sm mb-4">
            <span className="text-white/50 uppercase tracking-widest">Status</span>
            <span className="flex items-center gap-2 font-bold text-white" data-testid="fulfillment-status">
              {isFulfilling ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : (
                <StatusIcon className="w-4 h-4 text-primary" />
              )}
              {statusInfo.label}
            </span>
          </div>

          {/* Delivery */}
          {order.shipping_method_label && (
            <div className="flex justify-between items-center text-sm mb-4" data-testid="order-delivery">
              <span className="text-white/50 uppercase tracking-widest">Delivery</span>
              <span className="font-bold text-white text-right">
                {order.shipping_method_label}
                {order.shipping_estimate && <span className="block text-[11px] text-white/40 font-normal normal-case tracking-normal">Arrives in {order.shipping_estimate}</span>}
              </span>
            </div>
          )}

          {/* Tracking */}
          {tracking?.number && (
            <div className="border-t border-white/10 pt-4 mb-4" data-testid="tracking-block">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-white/50 uppercase tracking-widest">Carrier</span>
                <span className="font-bold text-white">{tracking.carrier || "—"}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-white/50 uppercase tracking-widest">Tracking</span>
                {tracking.url ? (
                  <a
                    href={tracking.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tracking-link"
                    className="flex items-center gap-1.5 font-bold text-primary hover:text-white transition-colors"
                  >
                    {tracking.number}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="font-bold text-white">{tracking.number}</span>
                )}
              </div>
            </div>
          )}

          {/* Line items with personalization */}
          {Array.isArray(order.line_items) && order.line_items.length > 0 && (
            <div className="border-t border-white/10 pt-4 space-y-3" data-testid="order-items">
              {order.line_items.map((li, i) => (
                <div key={`${li.product_id}-${li.variant_id}-${i}`} className="flex gap-3 items-center">
                  {li.image && (
                    <img src={li.image} alt={li.title} className="w-12 h-14 object-contain bg-neutral-900 rounded-sm shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white text-sm truncate">{li.title}</p>
                    <p className="text-[11px] text-white/40 uppercase tracking-widest">{li.size} · Qty {li.quantity}</p>
                    {(li.custom_name || li.custom_number) && (
                      <p className="text-[11px] text-primary uppercase tracking-widest mt-0.5">
                        {li.custom_name}{li.custom_name && li.custom_number ? " · " : ""}
                        {li.custom_number ? `#${li.custom_number}` : ""}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-white whitespace-nowrap">{formatPrice(li.price * li.quantity)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between text-sm border-t border-white/10 mt-4 pt-4">
            <span className="text-white/50 uppercase tracking-widest">Total</span>
            <span className="font-bold text-white">{formatPrice(order.total_price)}</span>
          </div>

          {order.points_earned > 0 && (
            <div data-testid="points-earned" className="mt-4 flex items-center justify-center gap-2 bg-primary/10 border border-primary/30 py-3 text-sm font-bold text-primary uppercase tracking-widest">
              <Gift className="w-4 h-4" /> You earned {order.points_earned.toLocaleString()} points
            </div>
          )}

          {isFulfilling && (
            <p className="text-xs text-white/40 mt-4">
              We're sending your personalized jersey into production now — tracking will appear here and in your inbox once it ships.
            </p>
          )}
          {statusKey === "printify_failed" && (
            <p className="text-xs text-primary mt-4">
              Payment received. This product isn't linked in Printify yet — our team will fulfill it manually.
            </p>
          )}
        </div>
      )}

      <div className="text-center">
        <Link to="/#shop" className="inline-block mt-10 bg-primary text-black hover:bg-white px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm transition-colors">
          Keep Shopping
        </Link>
        {order?.id && (
          <div className="mt-5">
            <Link to={`/track?id=${order.id}`} data-testid="success-track-link" className="text-primary underline underline-offset-4 font-bold uppercase tracking-widest text-xs hover:text-white transition-colors">
              Track this order anytime
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
